import { FormlyFieldConfig, FormlyFieldProps } from '@ngx-formly/core';
import { ContextService, LayoutInterface, OptionSet } from '../../context.service';
import { OptionPatterns, StepDefinition } from '../dynamic';
import { CaptureFormComponent } from './capture-form.component';
import { BehaviorSubject, debounceTime, map, filter, distinctUntilChanged, Subscription, tap } from 'rxjs';
import { AbstractControl, FormControl } from '@angular/forms';


// This helper class can be used to trigger a function 
export class FieldFunction {
  text: string | undefined;
  triggerFunction: Function | undefined;
  context: ContextService;
  ui: ControlDataFinder | undefined;

  constructor(
    private form: CaptureFormComponent,
    public field: FormlyFieldConfig,
    functionLabel = '_onChanges',
    functionText = field.props?.[functionLabel]) {
    // Shortcut to the incoming context object (allows this to subscribe to page level changes)
    this.context = this.form.context;
    try {
      this.triggerFunction = new Function('value', this.text = functionText);
    } catch (err) {
      console.error('Syntax error key => %O:\n  %O\n%c%O', field.key, this.text, "color:darkred", err);
    }
  }
  get model(): any | undefined {
    return this.form.step?.dataModel;
  }
  /* Here is where we build the functions accessible from the trigger function at execution time */
  /**
   * call the trigger, then manually update validity
   * @param value 
   */
  callFunction(value: any) {
    if (!this.ui) {
      if (this.form.step && this.form.step.formlyOptions) { this.ui = this.form.step.formlyOptions.formState.ui; }
    }
    try {
      value = this.triggerFunction?.call(this, value);
    } catch (err: any) {
      console.error('%O Function %O\nError %O\n%O', this.field.key, this.text, err.message, err.stack);
    }
    // this.context.updateValidity();
    return value;
  }


  // Default birthdate value (should be converted to exported function of forms)
  guessBirth() {
    let today = new Date(Date());
    today.setFullYear(today.getFullYear() - 15);
    return today;
  }

}

/**
 * Main class to manage and manipulate data. The data tree structure is converted to the UI
 * equivalent based on the field definitions. 
 */
export class ControlDataFinder {
  text: string | undefined;
  triggerFunction: Function | undefined;
  private optionSet: OptionSet | undefined;
  private appPages: StepDefinition[] | undefined;
  private deferredEnabled: any[] = [];
  private deferredDisabled: any[] = [];
  private subscriptions: Subscription[] = [];

  constructor(
    public pageComponentInstance: CaptureFormComponent) {

    this.optionSet = this.context.config?.lookups
    this.appPages = pageComponentInstance.stepDefinitions;
    // Enable/Disable validity check
    this.subscriptions.push(this.context.subscribeTo("checkValidity", {
      next: () => {
        let later: any[] = [];
        this.deferredEnabled.forEach((def: any) => {
          let config = this.getFieldConfig(def.section, def.control);
          if (!!config) {
            config?.props?.sortableOptions?.enable(def.values, def.selected);
          } else {
            later.push(def);
          }
        });
        this.deferredEnabled = later;
        later = [];
        this.deferredDisabled.forEach(def => {
          let config = this.getFieldConfig(def.section, def.control);
          if (!!config) {
            config?.props?.sortableOptions?.disable(def.values, def.selected);
          } else {
            later.push(def);
          }
        });
        this.deferredDisabled = later;
      }
    }));
  }

  get debug() {
    return !!this.pageComponentInstance.debug;
  }
  /** 
   * Shortcut to give access to the ContextSettings of the overall application
   */
  get context() {
    return this.pageComponentInstance.context;
  }

  get stepDefinitions() {
    return this.appPages;
  }

  destroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  getStaticOptions(optionKey: string): any[] {
    let options = this.optionSet?.[optionKey] ?? [];
    return [...options]; //.map((element: OptionDetail) => { return { id: element.code, name: element.description } });
  }
  getLayout(layoutKey: string): LayoutInterface | null | undefined {
    return this.context?.findLayout(layoutKey);
  }

  getStep(stepKey: string): StepDefinition | undefined {
    return this.pageComponentInstance?.stepDefinitions?.find(step => (step.step == stepKey));
  }

  getFieldProperty(section: string, control: string, property: string) {
    let config = this.getFieldConfig(section, control);
    return config?.props?.[property];
  }
  /**
   * Allow us to get the formControl for the section field
   * @param section 
   * @param control 
   */
  getControl(section: string | undefined, control: string | undefined = section?.split('|')?.[1]) {
    let fieldControl: AbstractControl | undefined;
    try {
      section = section?.split('|')?.[0];
      fieldControl = this.getFieldConfig(section, control)?.formControl;
    } catch (err) {
      console.error('No formcontrol found for field %O:%O', section, control);
    }
    return fieldControl;
  }

  setRequired(section: string, control: string, value: boolean) {
    let config: FormlyFieldConfig | undefined = this.getFieldConfig(section, control);
    if (config?.props) {
      config.props.isRequired = !!value;
    }
  }

  /**
   * Walk a JSON path to find the requested FormlyFieldConfig 
   * @param controlPath 
   */
  getFieldConfig(section: string | undefined, control: string | undefined = section?.split('|')?.[1]): FormlyFieldConfig | undefined {
    try {
      section = section?.split('|')?.[0];
      let step: StepDefinition | undefined = this.appPages?.find(step => step.step == section);
      return (!!step && !!step.fieldMaps && control ? step.fieldMaps[control] : {});
    } catch (err) {
      console.error(err);
    }
  }

  /**
   * Set options based on parent category
   * 
   * @param lookup 
   * @param category 
   */
  slaveOptions(lookup: string, category: string): any[] {
    let options = [...this.getStaticOptions(lookup)].filter(option => {
      if (option.category === category) {
        return true;
      }
      let valueOfOption = option.value.indexOf('-') > -1 ? option.value.split('-')[0] : option.value,
        categoryPart = category?.indexOf('-') > -1 ? category.split('-')[category.split('-').length - 1] : category;
      option.label = option.label.indexOf('-') > -1 ? option.label.split('-')[1] : option.label;

      return (valueOfOption === categoryPart);
    });
    return options;
  };

  isValid(section: string | undefined, control: string | undefined) {
    let fConfig = this.getFieldConfig(section, control), valid = !fConfig?.formControl?.errors && !!fConfig?.formControl?.value;
    return valid;
  }

  selectedProperty(section: string | undefined, control: string | undefined, property: string | undefined): FormlyFieldConfig | undefined {
    return this.getFieldConfig(section, control)?.props?.sortableOptions?.selectedOption?.[property ?? ''];
  }
  getOptionDate(section: string | undefined, control: string | undefined, optionName: string = 'sort') {
    let fc = this.getFieldConfig(section, control), result = null;
    if (!!fc) {
      if (['radio', 'select', 'radio-group'].includes(<string>fc.type)) {
        let selectedOption = (<any[]>fc.props?.options).find(opt => opt.value == fc.props?.sortableOptions?.selectedValue);
        if (!!selectedOption) {
          result = new Date(selectedOption[optionName] ?? 0).getTime();
        }
      }
    }
    return result;
  }

  getKeyValue(section: string | undefined, record: string | undefined, field: string | undefined) {
    let current: StepDefinition | undefined = this.appPages?.find(step => step.step == section),
      value;
    if (!!current) {
      let recordMap = current.keyMaps[record ?? ''];
      if (!!recordMap) {
        value = recordMap[field ?? ''];
      }
    }
    return value;
  }
  getAge(section: string | undefined, control: string | undefined, dtCompare: number | null = null) {
    let age = -1, startDate = this.getValue(section, control) ?? null;
    if (!dtCompare) {
      dtCompare = (new Date()).getTime();
    }
    if (!!startDate) {
      if (typeof startDate == 'string') {
        startDate = new Date(startDate);
      }
      try {
        //console.log('getAge %O', startDate);
        age = Math.floor((dtCompare - startDate.getTime()) / DateOptions.YEAR);
      } catch (e) {
        console.log('Invalid date value %O.%O\n%O', section, control, e);
      }
    }
    return age;
  }

  /**
   * Get the value from the field DOM interface or fallback to the model
   * 
   * @param section page of the application
   * @param control identifier of the ui control in Mutara group.rec-field.subfield
   * @returns 
   */

  getValue(section: string | undefined, control: string | undefined = section?.split('|')?.[1]): any {
    try {
      section = section?.split('|')?.[0];
      let fc: FormlyFieldConfig | undefined = this.getFieldConfig(section, control), value: any;

      if (!!fc?.formControl && fc.type != 'radio-group') {
        return fc.formControl.value;
      }
      if (!!fc) {
        value = fc.props?.getValue();
      } else {
        // console.log('getValue %O.%O no control yet', section, control);
        value = this.appPages?.find(stepDefn => stepDefn.step == section)?.dataModel;
        control?.split('.').forEach(layer => {
          try {
            if (!!value && Object.keys(value).length > 0) {
              value = value[layer];
            } else {
              value = null;
            }
          } catch (e) {
            console.error(e);
          }
        });
      }
      return value;
    } catch (err) {
      console.error('unexpected error ControlDataFinder.getValue %O|%O\n%O', section, control, err);
    }
  }
  // WIP
  displayField(section: string | undefined, control: string | undefined, hidden: boolean) {
    let fc: FormlyFieldConfig | undefined = this.getFieldConfig(section, control);
    if (fc && fc.props) {
      fc.props.forceHide = !!hidden;
      console.log('%O -> %O', control, fc.hide);
    }
  }

  resetValue(section: string | undefined, control: string | undefined, force: boolean = false) {
    if (this.debug) { console.log('resetValue %O.%O', section, control); }
    this.setValue(section, control, this.getValue(section, control), force);
  }

  /**
   * Set any value in the current formly structure
   * @param section
   * @param control 
   * @param value 
   * @param triggerSave 
   */
  setValue(section: string | undefined, control: string | undefined, value: any, triggerSave: boolean = false) {
    let fieldConfig = this.getFieldConfig(section, control), step = this.appPages?.find((step) => step.step == section);
    if (!fieldConfig || !fieldConfig.props) {
      console.error('INVALID FIELD %O TRYING TO SET VALUE %O', `${section}.${control}`, value);
      return;
    }
    /*
    if (!!fieldConfig?.props?.multiple) {
      try { value = JSON.parse(value); } catch (e) { }
    }
    */
    // If we have a formControl, set the value via Formly
    if (!!fieldConfig.formControl) {
      // Clear errors to ensure the upcoming blur/change will send data
      fieldConfig.formControl.setErrors(null);

      switch (fieldConfig.type) {
        case 'select':
          if (!fieldConfig?.props?.multiple && !value) {
            if (((<any[]>fieldConfig.props?.options)?.length ?? 0) == 1) {
              value = (<any[]>fieldConfig.props?.options)?.[0].value;
            } else {
              value = (!!(<any[]>fieldConfig.props?.options ?? []).find(opt => opt.value == value) ? value : fieldConfig.props?.valueOnHide ?? null);
            }
          } else {
            if (typeof value == 'string') {
              try { value = JSON.parse(value); } catch (e) { }
            }
          }
        //fieldConfig.props?.sortableOptions.selectedValue = value;
        default:
          // This may not send the value if the field is currently hidden
          fieldConfig.formControl.setValue(value);
          break;
      }
    } else if (!!step) {
      let model: any = step.dataModel, parts = (<string>control).split('.'), lastLayer: string | undefined = parts.pop();
      parts.forEach((layer: string) => {
        if (!!model && model[layer] !== undefined) {
          if (!model[layer] && !!value) {
            model[layer] = {};
          }
          model = model[layer];
        } else {
          model = null;
        }
      });
      if (!!model && lastLayer) {
        model[lastLayer] = value;
      } else {
        step.dataModel[parts.pop() + '.' + lastLayer] = value;
        //     console.error('Cannot set value in %O|%O', section, control);
      }
      // console.log('NO STEP %O Field %O set', step, fieldConfig.key);
    }
    // If there is a field, trigger the change event - (maybe unnecessary)
    // console.groupCollapsed(control);
    // console.log('triggering with value %O', value);
    // console.log('called by %O', (new Error()).stack);
    if (!!triggerSave) {
      this.pageComponentInstance.__triggerDataSave(fieldConfig, { value: value, forceSave: triggerSave });
    }
    // console.groupEnd();
    this.pageComponentInstance.cdr.markForCheck();
  }

  /**
   * Enable only those values that are passed. If nothing is passed, the whole list is enabled 
   * @param section string
   * @param control string
   * @param values string[] 
   */
  enableOptions(section: string | undefined, control: string | undefined, values: any[] = [], selected: string | undefined = undefined) {
    let config = this.getFieldConfig(section, control);
    if (!!config) {
      config.props?.sortableOptions?.enable(values, selected);
    } else {
      this.deferredEnabled.push({ section: section, control: control, selected: selected });
    }
  }

  disableOptions(section: string | undefined, control: string | undefined, values: any[] = [], selected: string | undefined = undefined) {
    let config = this.getFieldConfig(section, control);
    if (!!config) {
      config.props?.sortableOptions?.disable(values, selected);
    } else {
      this.deferredDisabled.push({ section: section, control: control, selected: selected });
    }
  }
  resetOptions(section: string | undefined, control: string | undefined) {
    this.disableOptions(section, control);
  }

  setValidator(section: string | undefined, control: string | undefined, setName: string, code: string, name: string) {
    let option: any = this.getStaticOptions(setName)?.filter(option => option.value == code);
    if (option.length > 0) {
      let regExprText = option[0][name ?? ''];
      let step = this.appPages?.find((stepDefn) => stepDefn.step == section);
      if (!!step) {
        step.validations[control ?? ''] = new RegExp(!!regExprText ? regExprText.trim() : '.*');
        let cntl = this.getControl(section, control);
        if (!!cntl) {
          cntl.updateValueAndValidity();
        }
      }

    }
  }
}

/**
 * Class that handles select (name/value) and table options (id/data)
 * Takes a field config, global option set, and data finder 
 */
export class DynamicOptions {
  private recordName: string = '';
  private keyValues: BehaviorSubject<any> = new BehaviorSubject<any>({});
  private lookupValues: any = {};
  private cntlSubs: Subscription[] = [];
  private fieldsToWatch: any[] = [];
  private watchedFields: any[] = [];
  private context: ContextService | undefined;
  private defaults: any;
  private isSubscribed: boolean = false;
  private isInitialized: boolean = false;
  private isRunning: boolean = false;
  disabled: string[] = [];
  public obsOptions: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  constructor(private field: FormlyFieldConfig, private ui: ControlDataFinder | undefined) {
    this.context = this.ui?.pageComponentInstance.context;
    this.defaults = this.context?.config?.defaults;
    this._initializeOptions();
  }

  get sortDefn(): any {
    return this.field.props?.sort;
  }

  get rank(): string {
    return this.sortDefn?.rank ?? [];
  }

  get on(): number {
    return this.sortDefn?.on ?? 'label';
  }

  get size(): number {
    return this.sortDefn?.size ?? 5;
  }

  get selectedOption(): any {
    let val = this.selectedValue;
    return this.obsOptions?.value?.find(opt => opt.value == val);
  }

  get selectedValue(): any {
    let value: any;
    if (!!this.field.model) {
      value = this.field.model;
      try {
        let parts = (<string>this.field.key).split('.');
        parts.forEach(part => value = (!!value ? value[part] : null));
      } catch (e) {
        console.error('Problem selecting value %O', e);
      }
    }
    return value;
  }

  set selectedValue(val: any) {
    if (!!this.field.model) {
      let parts: string[] = (<string>this.field.key).split('.'), value: any = this.field.model, last: string | undefined = parts.pop();
      parts.forEach(part => value = value[part]);
      if (value && last) { value[last] = val; }
    }
  }

  get fieldName(): string {
    return <string>this.field.key;
  }
  /**
   * Shortcut to props
   */
  get props() {
    return this.field?.props;
  }

  /**
   * Shortcut to the lookup definition
   */
  get lookupRemote() {
    return this.field?.props?.lookupRemote;
  }

  triggerLookup() {
    this.watchedFields.forEach((field: { key: string, fc: FormlyFieldConfig }) => {
      const curValue = field.fc.props?.getValue();
      if (this.lookupValues[field.key] != curValue) {
        this.lookupValues[field.key] = curValue;
      }
    });
    if (this.debug) { console.log('Manually trigger a lookup %O\n%O', this.fieldName, this.lookupValues); }
    this.keyValues.next(this.lookupValues);
    this.markForCheck();
  }

  markForCheck() {
    this.field.props?.capForm?.cdr?.markForCheck();
  }
  get debug(): boolean {
    return !!this.field.props?.capForm?.debug;
  }
  /** 
   * At construction, we can setup the grouping of select options, get 
   * arrays of static options, setup initial slaving from the initiOptionCategory,
   * add empty value if designated in the defintion (precision)
   */
  _initializeOptions() {
    if (!!this.props?.groupPattern) {
      // There will be three values that are derived from the element object
      this.__createGroupingDefinition();
    }
    // If the lookupRemote finds no static values, create the empty one
    let optionArray: any[] = [];
    try {
      optionArray = [...this.ui?.getStaticOptions(this.lookupRemote?.static) ?? []];
    } catch (err) {
      console.error('Problem with options %O\n  %O', this.lookupRemote, err);
    }
    let isSlave = false;
    // If there is a starting category, make sure we select it first (jump forward in list)
    if (!!this.props?.initialOptionCategory) {
      let testValue = !/^\'.*\'$/.test(this.props?.initialOptionCategory) ?
        this.ui?.getValue(this.props?.stepName, this.props?.initialOptionCategory) : // data value
        this.props?.initialOptionCategory.substr(1, this.props?.initialOptionCategory.length - 2); // fixed value
      optionArray = optionArray.filter(option => option?.category == testValue);
    }

    if (!!this.props?.multiple) {
      try { this.selectedValue = JSON.parse(this.selectedValue); this.field.props?.setValue(this.selectedValue); } catch (e) { }
    }

    // Set the option array to enable/disable, sort, etc.
    if (!!this.lookupRemote?.static) {
      this.setOptions(optionArray);
      this.isInitialized = true;
    } else if (this.props) {
      this.props.options = [];
    }
  }

  /**
  * Walk the current option object, to return a correctly formatted grouped option
  * @param optionData 
  * @param optionPattern 
  */
  __createGrouping(optionData: any): any {
    let option: any = {}, bEmpty: boolean = false;

    for (const [optionKey, pat] of Object.entries(this.props?.groupPattern)) {
      //Object.keys(this.props?.groupPattern).forEach((optionKey) => {
      let thePattern: OptionPatterns = <OptionPatterns>pat,
        valueList = thePattern.re.exec(optionData[optionKey]);
      if (!valueList && !bEmpty) {
        bEmpty = true; option = { value: ' ', label: '' };
      } else if (!bEmpty) {
        valueList?.splice(0, 1); // This clears the original value
        // Now walk the values and pack the data back into the option definition
        thePattern.values.forEach((valueKey: string) => {
          if (option)
            option[valueKey] = valueList?.shift();
          if (!!thePattern.mapping && !!thePattern.mapping[valueKey]) { // This is a function to be called with context... 
            let context: any = { values: option, options: this.ui?.pageComponentInstance?.appConfig?.lookups };
            try {
              thePattern.mapping[valueKey].call(this, context);
            } catch (err) {
              console.error('Group mapping failed %O %O %O', this.props?.decoratedId, optionKey, err);
            }
          }
        });
      }
    }
    return option;
  }

  /**
    * Trick to convert a REGEX string to an RegExp object 
    */
  __createGroupingDefinition() {
    // Convert the regex string to a RegExp object
    for (let [, val] of Object.entries(this.props?.groupPattern)) {
      let definition: OptionPatterns = <OptionPatterns>val;
      try {
        // Convert the pattern to an object
        definition.re = new RegExp(definition.regex);
        // If there is a mapping, convert each to a callable function
        if (!!definition.mapping) {
          Object.keys(definition.mapping).forEach(key => {
            try {
              definition.mapping[key] = new Function('context', definition.mapping[key]);
            } catch (err) {
              console.error('Problem with group definition mapping function %O', err);
              delete definition.mapping[key];
            }
          });
        }
      }
      catch (error) {
        console.log('Problem with REGEXP for groupDefinition %O', this.props?.groupPattern);
      }
    }
  }

  /**
   * Used by sorting hat
   * @param left 
   * @param right 
   * @returns 
   */
  _orderTest(left: string | undefined, right: string | undefined): number {
    let val: number = 0;
    if (left === undefined && right === undefined) {
      return 0;
    }
    try {
      switch (this.sortDefn.order ?? 'a') {
        case 'n':
          val = 0;
          break;
        case 'd':
          val = right?.localeCompare(left ?? '') ?? 0;
          break;
        case 'a':
        default:
          val = left?.localeCompare(right ?? '') ?? 0;
          break;
      }
    } catch (err) {
      console.error('DynamicOptions._orderTest: %O %O', this.props?.decoratedId, err);
    }
    return val;
  }

  _substituteVariable(source: string) {
    return this.context?.substituteVariable(source);
  }
  /**
   * This sets a list of filters for the options and triggers a reload 
   * @param valueFilter 
   * @param [includeValues=true] inclusive if true, exclusive if false
   */
  setFilter(valueFilter: string[], includeValues: boolean = true) {
    let sameFilter: boolean = (valueFilter ?? []).every((val: string) => !!this.props?.filterList?.includes(val)) && valueFilter?.length == this.props?.filterList?.length;
    if (!sameFilter) {
      if (this.debug) { console.log('Filter (%O) from %O to %O', this.fieldName, this.props?.filterList, valueFilter); }
      if (this.props) {
        this.props.filterList = valueFilter;
        this.props.filterTest = includeValues;
      }
    }
    try {
      if (!sameFilter) {
        if (this.debug) { console.log('Filters updated, trigger lookup %O\n%O', this.fieldName, this.lookupValues); }
        this.keyValues.next(this.lookupValues); // Emit change
      }

      // if (!!valueFilter && (valueFilter.includes(this.selectedValue) != includeValues)) {
      //   if (this.debug ) {
      //     console.log('setFilter forcing a null for %O', this.fieldName);
      //   }
      //   this.props?.setValue(null);
      // }

    } catch (err) {
      console.error('%O error on lookup %O\n%O', this.fieldName, this.lookupValues, err);
    }
  }

  /**
   * Called after the changeValues we are watching
   * TODO: create an interface for the defn
   * @param newValue 
   * @param defn 
   */
  _processNewValue(newValue: any, defn: any) {
    if (this.debug) { console.log('_processNewValue %O=>(%O) changes %O', defn.key, newValue, this.fieldName); }
    if (!!defn.evaluate) {
      try {
        newValue = defn.evaluate.call(this);
      } catch (e) {
        console.error('Processing %O new value %O\n%O', this.fieldName, defn.evaluate, e);
      }
    }

    // Here we setup the new value and update the initialized flag
    if (this.lookupValues[defn.key] != newValue) {
      this.lookupValues[defn.key] = newValue;
      if (this.field.type != 'typeahead' || !!this.field.props?.rdv?.currentValue) {
        this.keyValues.next(this.lookupValues);
      }
    }
    if (!!defn.hide) {
      console.log('Need to set value for %O?', defn.key);
    }
    this.markForCheck();
  }
  /**
   * Setup the subscription to the remote values
   */
  setupSubscription() {
    if (!this.lookupRemote) {
      return;
    }
    // This section deals with group definitions from the db
    try {
      // replace any static variables in the URL
      this.lookupRemote.dynamic = this._substituteVariable(this.lookupRemote.dynamic);
      // Loop through the constraint keys and set default values and the array of 
      for (let key in this.lookupRemote.keys) {
        let value: any = this.lookupValues[key] = this._substituteVariable(this.lookupRemote.keys[key]);
        // Here we are looking at a single value and doing the subscription for the value
        if (/`[\w]*\|?[\w-\.]+`/.test(value)) {
          // clean off the ticks ``
          value = value.slice(1, -1);
          // look for the step name and find the field maps for the value
          let fullName = value.split('|'), step = this.ui?.pageComponentInstance.stepName;
          if (fullName.length > 1) {
            step = fullName.shift();
          }
          /* 
          This is where we are notified that all of the sections are loaded and can add subscriptions
          WARNING: make sure that setting values in the subscription do not re-trigger the subscription
          */
          /* the mapped name must be converted to be the current value before the subscription is set */
          // console.log('%O Initialize %O %O => %O value %O', this.props?.decoratedId, key, step, fullName[0], this.lookupValues[key]);
          this.fieldsToWatch.push({ key: key, step: step, fieldName: fullName[0], source: value, controlName: fullName[0].split('.', 2).pop() });
        } else if (/\s*\{[\s\S\w]*return[\w\s\W]*;\}\s*/.test(value)) {
          // Here we process the internal calls to find the fields to track 
          let fields = value.match(/\((?:'(?<section>\w*)'[,\s]*'(?<field>[\w.-]*)')/g);
          fields.forEach((fieldValue: string) => { // ('section','fieldref'
            let fullValue = fieldValue.slice(1).replace(/'/g, "").split(',');
            this.fieldsToWatch.push({ key: key, step: fullValue[0], fieldName: fullValue[1], evaluate: new Function(value), controlName: fullValue[1].split('.', 2).pop() });
          });
          // Needs to evaluate!!
          try {
            let func = new Function(value);
            this.lookupValues[key] = func.call(this);
          } catch (e) {
            console.log('Error Evaluating %O complex lookup value %O\n%O', this.fieldName, value, e);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
    // Clean up the base 
    delete this.lookupRemote.keys;
    // Here we get to add subscriptions to other values
    if (this.context) {
      if (!!this.lookupRemote?.static && !this.isSubscribed) {
        if (!!this.selectedValue) { this.field.formControl?.setValue(this.selectedValue, { onlySelf: true, emitEvent: false }); }
        this.setupKeyObserver();
        this.isSubscribed = true;
        return;
      }
      // Set this up only once if we are dependent on other values
      if (!this.isSubscribed && this.fieldsToWatch.length > 0) {
        this.fieldsToWatch.forEach(defn => {
          let fieldConfig: FormlyFieldConfig | undefined = this.ui?.getFieldConfig(defn.step, defn.fieldName);
          if (!fieldConfig) {
            console.error('NO FIELD %O|%O => %O', defn.step, defn.fieldName, defn.source);
            return;
          }
          this.lookupValues[defn.key] = fieldConfig.props?.getValue() ?? null;
          this.watchedFields.push({ key: defn.key, fc: fieldConfig });
          /**
           * Add the subscription to the individual fields value changes
           */

          if (!!fieldConfig.props?.sortableOptions) {
            this.cntlSubs.push(
              fieldConfig.props?.sortableOptions.obsOptions.subscribe({
                next: (opts: any) => {
                  if (this.debug) { console.log('%O options changing field %O\n%O', fieldConfig.props?.label, this.fieldName, opts); }
                  const val = fieldConfig.props?.getValue();
                  if (fieldConfig.id && val !== undefined) {
                    this._processNewValue(val, defn);
                  }
                }
              })
            );
          }
          if (fieldConfig.formControl) {
            this.cntlSubs.push(
              fieldConfig.formControl?.valueChanges.subscribe({
                next: (newValue: any) => {
                  // If there is a new value that is not an initialization or we are not loading
                  if ((!!newValue || (this.context?.getMessageValue('readyToReceive') && this.isSubscribed))) { //&& ((<any[]>fieldConfig.props?.options)?.length ?? -1) > 1
                    if (!fieldConfig.props?.sortableOptions || (fieldConfig.props?.sortableOptions && newValue != fieldConfig.props?.sortableOptions?.selectedValue)) {
                      this._processNewValue(newValue ?? fieldConfig.props?.sortableOptions?.selectedValue, defn);
                    }
                  }
                }
              })
            );
          }

        });
      }
      // Trigger a lookup/value setup if this is the first time
      if (!this.isSubscribed) {
        this.isSubscribed = true;
        // console.log('Initial trigger %O (%O) => %O', this.fieldName, this.selectedValue, this.lookupValues);
        let parameters: any = Object.assign({
          endUser: this.ui?.pageComponentInstance?.step?.endUser,
          source: this.lookupRemote.dynamic,
          keys: this.lookupValues,
          fields: null
        }, this.lookupRemote);

        switch (this.field.type) {
          case 'formly-group':
            parameters.limit = 200;
            break;
          case 'typeahead':
            parameters.limit = 50;
            break;
          default:
            parameters.limit = 0;
            break;
        }
        this.setupKeyObserver(parameters);
        if (this.field.type != 'typeahead') {
          this.keyValues.next(this.lookupValues);
        }
      }
    }
  }

  _isSame(x: any, y: any): boolean {
    const ok = Object.keys, tx = typeof x, ty = typeof y;
    return x && y && tx === 'object' && tx === ty ? (
      ok(x).length === ok(y).length &&
      ok(x).every(key => this._isSame(x[key], y[key]))
    ) : (x === y);
  }

  /**
   * Called at initialization to enable value subscriptions 
   * 
   * @param parameters 
   */
  setupKeyObserver(parameters: any = null) {
    // Any key values will get updated here and options get requested
    if (!!this.lookupRemote.dynamic) { //&& this.field.type != 'typeahead') {
      this.cntlSubs.push(this.keyValues.asObservable().pipe(debounceTime(70)).subscribe(updatedValues => {
        if (this.debug) { console.log('KeyObserver %c%O\n..%c%O', 'color:green', this.fieldName, 'color:black', updatedValues); }
        if (!!this.isRunning) {
          setTimeout(() => this.keyValues.next(updatedValues), 100);
          if (this.debug) {
            console.log('%O is already running lookup, reset in 100ms', this.fieldName);
          }
          return;
        }
        if (this.field.type == 'typeahead' && (updatedValues.term ?? '') == '') {
          return;
          // Bail out if there is no term since we don't want a mega-list
        }
        // Remember the starting selectedValue
        let _currentValue = this.selectedValue;
        let keysEmpty = (!updatedValues || Object.keys(updatedValues).length == 0) && !this.field.formControl?.disabled;
        parameters.keys = updatedValues ?? {};
        // Handle grouping if available
        if ((this.field?.fieldGroup?.length ?? 0) > 0) {
          parameters.fields = this.field.fieldGroup?.map(fld => fld.key);
        }

        if (!!this.lookupRemote.allKeyValues) {
          let hasNull = Object.values(updatedValues).some((value: any) => [null, undefined].includes(value));
          if (hasNull) {
            keysEmpty = true;
            this.isInitialized = true;
          }
        } else {
          keysEmpty = false;
        }

        if (!keysEmpty || this.fieldsToWatch.length == 0) {
          if (this.debug) {
            console.log('KeyObserver Request %c%O %c(%O)', 'color:green', this.fieldName, 'color:blue', parameters.keys);
          }
          this.isRunning = true;
          this.ui?.pageComponentInstance.http.post<any[]>('$service/lookup', parameters).subscribe(
            {
              error: (errorData) => {
                this.isRunning = false;
                console.error('key %O change for lookup error %O', parameters, errorData);
              },
              next: (data) => {
                if (this.debug) {
                  console.log('KeyObserver Response %c%O\n..%c%O', 'color:brown', this.fieldName, 'color:black', data);
                }
                if (this.debug && data == null) {
                  console.error('%cWARNING: %c%O %cNo data return, is source connected?', 'color:red', 'color:blue', this.fieldName, 'color:red');
                }
                if (!this.props) {
                  console.error('Properties not yet defined!! %O', this);
                  return;
                }
                this.isRunning = false;
                //    let isSelected = !!this.props?.options && !!((<any[]>this.props?.options).find(opt => { return this.selectedValue == opt.value; }));
                if (!keysEmpty && !!data && data.length > 100 && this.field.type == 'typeahead') {
                  this.props.description = 'Too many results, continue typing...';
                } else if (this.props?.description != (this.props?.origDescr ?? '')) {
                  this.props.description = this.props?.origDescr ?? '';
                }
                // When this is a table, push the result directly to the handler
                if (this.field.type == 'formly-group') {
                  this.props?.dataSubject.next(data);
                } else { // remote select value list
                  if (this.field.type == 'file') {
                    this.selectedValue = (_currentValue ?? '|').split('|')[0];
                  }
                  // Data?
                  if (!!data && data.length > 0) {
                    this.setOptions([...data]);
                    if (!data.find((row) => row.value == this.selectedValue)) {
                      if (data.length == 1) { // If there is ONLY one value, change the selected value
                        if (this.debug) {
                          console.log('Setting %csingleton value %c%O %c(%O)', 'color:orange', 'color:green', this.field.key, 'color:black', data[0].value);
                        }
                        this.selectedValue = this.field.model[<string>this.field.key] = data[0].value;
                        if (this.field.formControl) {
                          this.field.formControl.setValue(this.selectedValue)
                        } else {
                          this.props?.setValue(this.selectedValue, true);
                        }
                      } else if (this.field.type != 'typeahead') { // otherwise, set to empty to force a selection
                        if (this.debug) {
                          console.log('Reset %O to null to allow list selection', this.fieldName);
                        }
                        this.props?.setValue(this.selectedValue = null, true);
                      }
                    }

                  } else if (!!this.props?.defaultSelect) { // This handles the use case of disconnected with a local backup of select options
                    this.setOptions(!this.props?.ifEmptyDefault
                      ? []  // production 
                      : [...(this.ui?.getStaticOptions(this.props?.defaultSelect) ?? [])]
                    );
                  } else {
                    this.setOptions([]);
                  }
                }
                if (!!this.props?.postLoad) {
                  this.props?.postLoad.callFunction(this.selectedValue);
                }

                if (!this.props?.options && !this.props?.noEmptyReset) {
                  if (this.debug) {
                    console.log('Resetting %O to null after option reset', this.fieldName);
                  }
                  this.field.formControl?.patchValue(null);
                }
                this.isInitialized = true;
              }
            });
        }
      }));
    } else if (!!this.lookupRemote.static && !this.props?.initialOptionCategory) {
      this.cntlSubs.push(this.keyValues.asObservable().subscribe(() => {
        if (!!this.props?.forceEnable) {
          this.setOptions([...this.ui?.getStaticOptions(this.lookupRemote?.static) ?? []]);
        }
      }));
    }
  }
  /**
   * Get the option definition from the list
   * @param value 
   */
  getOption(value: string): any {
    return (<any[]>this.props?.options).find(opt => opt.value == value);
  }

  /**
   * This takes an incoming list and decorates/arranges it by changing the grouping, sorting, etc
   * @param options rawOption list
   */
  setOptions(options: any[] = this.props?._startingOptions) {
    try {
      if (this.debug && this.lookupRemote.dynamic) { console.log('.setOptions %c%O\n%c..%O', 'color:orange', this.fieldName, 'color:black', [...options].slice(0, 20)); }
      // Jump out if not an array or the field is not initialized
      if (!this.props || !options) {
        return;
      }
      this.props._startingOptions = options;
      if (!!this.props?.filterList && this.props?.filterList?.length > 0) {
        if (this.debug) { console.log('remove options based on list %O %O', this.fieldName, this.props?.filterList); }
        let filtered: any[] = options.filter((opt) => {
          return this.props?.filterList.includes(opt.value) == (this.props?.filterTest ?? true);
        });
        options = filtered;
      }
      // optionsFilter eliminates options we don't need to see
      if (!!this.field.props?.optionFilter && typeof this.field.props?.optionFilter == 'function') {
        if (this.debug) { console.log('removing options based on function %O', this.fieldName); }
        options = this.field.props?.optionFilter.callFunction(options);
      }

      // Use the calculated disabled list to set the flag on the incoming options
      options.forEach(option => {
        option.disabled = (this.disabled.length > 0 && this.disabled.includes(option.value));
      });

      // now we can sort the array (if we need to!)
      if (!!this.props?.sort) {
        options = this.sortOptions(options);
      }
      // groupings are processed here
      if (!!this.props?.groupPattern) {
        options = options.map((element) => {
          return this.__createGrouping(element);
        });
      }

      options.forEach(opt => {
        let matches = opt?.label?.match(/(?:{{(?<default>\w*)}})/g), newValue;
        if (!!matches) {
          matches.forEach((matchValue: string) => {
            opt.label = opt.label.replace(matchValue, this.defaults[matchValue.slice(2, -2)]);
          });
        }
      });

      // In the case of a static array, precision of -1 means to add an empty in the array
      if (this.props?.precision == -1 && !options.find(opt => opt.value == null)) {
        options.unshift({ value: null, label: '' });
      }

      this.props.options = options;
      this.obsOptions.next(options);

      // Not in the list && isInitialized && all watched fields are initialized
      let currentValue = this.props?.getValue();
      if (!['radio-group', 'typeahead'].includes(<string>this.field.type)
        && !options.find(opt => opt.value == currentValue) && this.isInitialized
        && !!this.watchedFields.find(fld => !fld.props?.sortableOptions || !fld.props?.sortableOptions.isInitialized)) {
        if (this.debug) { console.log('SET to NULL, not an option %O(%O)', this.fieldName, currentValue); }
        this.field.props?.setValue(null);
      }

      if (!!this.props?.hideOnEmpty) {
        this.props.forceHide = (options.length < (this.field.type == 'select' ? 2 : 1));
      }

      if (options.length == 1 && this.field.type != 'radio-group') {
        this.field.props?.setValue(options[0].value, true);
      }

      // else {
      //   console.log('%O (%O) Options %O', this.fieldName, this.field.hide, options);
      // }
    } catch (err) {
      console.error('setOptions error %O', err);
    }
  }

  /**
   * enable only specific any values in a defaulted disabled list and refresh
   * @param valueList 
   * @param selected 
   */
  enable(valueList: string[], selected: string | undefined = undefined) {
    // if the list is empty, enable everything (remove disabled)
    this.disabled = !!this.props?.options && valueList.length > 0 ? (<any[]>this.props?.options).map(option => option.value) ?? [] : [];
    // Reset the disabled list to be only the values not passed
    if (!!this.disabled && valueList.length > 0) {
      this.disabled = this.disabled.filter(key => !valueList.includes(key));
    }
    if (!!this.props && !!this.props?.options) {
      this.setOptions([...(<any[]>this.props?.options)]);
    }
    if (!!selected) {
      this.selectedValue = selected;
      this.ui?.setValue(this.ui?.pageComponentInstance.stepName, this.props?.decoratedId, this.selectedValue);
    }
  }

  /**
   * Provide a list of values to grey out 
   * @param valueList 
   * @param selected 
   */
  disable(valueList: string[], selected: string | undefined = undefined) {
    this.disabled = valueList ?? [];
    if (!!this.props && !!this.props?.options) {
      this.setOptions([...(<any[]>this.props?.options)]);
    }
    if (!!selected) {
      this.selectedValue = selected;
      this.ui?.setValue(this.ui?.pageComponentInstance.stepName, this.props?.decoratedId, this.selectedValue);
    }
  }

  getOrderParam() {
    return { record: this.recordName, field: this.fieldName, rank: this.rank }
  }

  /**
   * Sort the options within the array
   *
   * @param optionArray incoming array to sort
   * @returns 
   */
  sortOptions(optionArray: any[]): any[] {
    let sorted = optionArray?.sort((a, b) => {
      let left = a[this.on], right = b[this.on],
        leftPos = this.rank.indexOf(left?.substring(0, this.size)),
        rightPos = this.rank.indexOf(right?.substring(0, this.size)); // this allows us to do math on ranked positions
      if (leftPos != -1 && rightPos != -1) {// Both in list
        let val = leftPos - rightPos;
        if (val != 0) {
          return val;
        }
      } else if (leftPos != -1) { // Left is before non-listed
        return -1;
      } else if (rightPos != -1) { // Right is before non-listed
        return 1;
      }
      return this._orderTest(left, right);
    });
    return sorted;
  }
  destroy() {
    this.cntlSubs.forEach(sub => { if (!!sub) { sub.unsubscribe(); } });
    this.cntlSubs = [];
  }
}
/**
 * static functions for managing dates in Javascript
 */
export class DateOptions {
  static YEAR = (365 * 86400000);
  static dateCalculation(startDate: Date, defn: any) {
    if (!startDate) { return null; }
    let calculatedDate = new Date(startDate?.getFullYear(), startDate?.getMonth(), startDate.getDate());
    Object.keys(defn).forEach(key => {
      switch (key) {
        case 'year':
          calculatedDate = new Date(calculatedDate.setFullYear(calculatedDate.getFullYear() + defn[key]));
          break;
        case 'month':
          calculatedDate = new Date(calculatedDate.setFullYear(calculatedDate.getFullYear(), calculatedDate.getMonth() + defn[key]));
          break;
        case 'day':
          calculatedDate = new Date(calculatedDate.setFullYear(calculatedDate.getFullYear(), calculatedDate.getMonth(), calculatedDate.getDate() + defn[key]));
          break;
      }
    });
    return calculatedDate;
  }

  static dateToUTCStorage(locale: string, dateToConvert: Date) {
    return dateToConvert;//(new Date(dateToConvert)).toLocaleDateString();
  }

  /// Called at Form definition creation time
  static options(config: FormlyFieldConfig): any {
    if (config.props) {
      config.props.datepickerOptions = {
        dateChange: (formly: FormlyFieldConfig, $event: any) => {
          formly.formControl?.setValue($event.value);
          if (formly.props?.change) { formly.props.change(formly, $event) }
        },
        dateInput: (formly: FormlyFieldConfig, $event: any) => {
          /*
          relativeSource = {  
            min : {
              referenceStep? : <string>,
              referenceDate : <decorated key>,
              duration : {
                <type> : <duration>
              },
              target:<decorated key>
            }, 
            max : {
              referenceStep? : <string>,
              referenceDate : <decorated key>,
              duration : {
                <type> : <duration>
              },
              target: <decorated key>
            }
          }
          */
          // If there is a relativeSource, we are getting asked to look at the current value of the source and check it
          if (!!formly.props?.relativeSource) {
            if (!!formly.props?.relativeSource.min) {
              let minDef = formly.props?.relativeSource.min,
                targetConfig = formly.props?.ui?.getFieldConfig(minDef.referenceStep ?? formly.props?.stepName, minDef.referenceDate);
              if (!!targetConfig && formly.props?.datepickerOptions) {
                formly.props.datepickerOptions.min = DateOptions.dateCalculation(targetConfig.props?.getValue(), minDef.duration);
              } else {
                console.error('No Target Config (min) %O', minDef.target);
              }
            }
            if (!!formly.props?.relativeSource.max) {
              let maxDef = formly.props?.relativeSource.max,
                targetConfig = formly?.props.ui?.getFieldConfig(maxDef.referenceStep ?? formly.props?.stepName, maxDef.referenceDate);
              if (!!targetConfig && formly.props?.datepickerOptions) {
                formly.props.datepickerOptions.max = DateOptions.dateCalculation(targetConfig.props?.getValue(), maxDef.duration);
              } else {
                console.error('No Target Config (min) %O', maxDef.target);
              }
            }
          }
        },
        // functions:
        //   dateInput (test it)
        //   dateChange (save it)

      }
      /*
      relativeDate {
        min : {
          referenceStep? : <string>,
          referenceDate : <decorated key>,
          duration : {
            <type> : <duration>
          },
          target : <decorated key>
        }, 
        max : {
          referenceStep? : <string>,
          referenceDate : <decorated key>,
          duration : {
            <type> : <duration>
          },
          target : <decorated key>
        }
      }
      */

      /**
       * Make date values valid/invalid by looking at other values
       */
      if (!!config.props?.relativeDate) {
        if (!!config.props?.relativeDate.min) {
          let minDef = config.props?.relativeDate.min, source: FormlyFieldConfig | undefined = config.props?.ui?.getFieldConfig(minDef.referenceStep ?? config.props?.stepName, minDef.referenceDate);
          minDef.target = config.props?.decoratedId;
          if (source?.props) { source.props.relativeSource = { "min": minDef }; }
        }
        if (!!config.props?.relativeDate.max) {
          let maxDef = config.props?.relativeDate.max, source: FormlyFieldConfig | undefined = config.props?.ui?.getFieldConfig(maxDef.referenceStep ?? config.props?.stepName, maxDef.referenceDate);
          maxDef.target = config.props?.decoratedId;
          if (source?.props) { source.props.relativeSource = { "max": maxDef }; }
        }
      }
      // These are the initial values based on the existing request, no fallback on minimum dates
      if (config.props && !!config.props?.minDate) {
        config.props.datepickerOptions.min = DateOptions.dateCalculation(new Date(), config.props?.minDate);
      }
      // If there is a pulse, don't allow dates in the far future
      if (config.props) {
        config.props.datepickerOptions.max = DateOptions.dateCalculation(new Date(), config.props?.maxDate ?? { year: 10 });
      }
    }
  }
}
